import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GetDomainPipe } from '@app/modules/direct/pipes/get-domain.pipe';
import { DirectSite } from '@app/shared/interfaces';
import { PropertiesService, SimpleProperty } from '@app/shared/services/properties/properties.service';
import { environment } from '@env/environment';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface CustomSite {
  uuid: string;
  widgets_enabled: boolean;
  domain: string;
  booking_widget_uuid: string;
  properties: { id: number; url: string | null }[];
  property_ids: number[];
  property_list?: SimpleProperty[];
  theme: string | null;
  styles: {
    primary_color: string | null;
    secondary_color: string | null;
    tertiary_color: string | null;
  };
}

@Injectable({
  providedIn: 'root',
})
export class CustomSitesService {
  constructor(
    private http: HttpClient,
    private propertiesService: PropertiesService,
    private getDomainPipe: GetDomainPipe
  ) {}

  getCustomSites() {
    const customSites$ = this.http.get<{ data: CustomSite[] }>(`${environment.siteApiUrl}/custom`);

    const properties$ = this.propertiesService
      .getProperties({
        filterCriteria: null,
        paginate: false,
        transformer: 'simple',
      })
      .pipe(map((res: { data: any[] }) => res.data));

    return combineLatest([customSites$, properties$]).pipe(
      map(([widgetsResponse, properties]) => {
        return this.mapCustomSites(this.mergeProperties(widgetsResponse.data, properties));
      })
    );
  }

  getCustomSite(uuid: string): Observable<CustomSite> {
    return this.http.get<{ data: CustomSite }>(`${environment.siteApiUrl}/custom/${uuid}`).pipe(
      map((res) => {
        res.data.property_ids = res.data.properties.map((property) => property.id);
        return res.data;
      })
    );
  }

  addCustomSite(): Observable<CustomSite> {
    return this.http
      .post<{ data: CustomSite }>(`${environment.siteApiUrl}/custom`, { widgets_enabled: true, theme: 'multi' })
      .pipe(map((res) => res.data));
  }

  updateCustomSite(uuid: string, site: CustomSite): Observable<CustomSite> {
    return this.http
      .put<{ data: CustomSite }>(`${environment.siteApiUrl}/custom/${uuid}`, site)
      .pipe(map((res) => res.data));
  }

  renameCustomSite(uuid: string, name: string): Observable<CustomSite> {
    return this.http
      .post<{ data: CustomSite }>(`${environment.siteApiUrl}/custom/${uuid}/rename`, { name: name })
      .pipe(map((res) => res.data));
  }

  deleteCustomSite(uuid: string): Observable<any> {
    return this.http.delete(`${environment.siteApiUrl}/custom/${uuid}`);
  }

  updateSitePropertyUrl(siteId: string, propertyId: number, url: string): Observable<any> {
    return this.http.put(`${environment.siteApiUrl}/custom/${siteId}/modify-property-url/${propertyId}`, { url });
  }

  validatePropertyUrl(siteUuid: string, propertyId: number): Observable<any> {
    return this.http.get(`${environment.siteApiUrl}/custom/${siteUuid}/property/${propertyId}/health`);
  }

  mapCustomSites(sites: any[]): DirectSite[] {
    return sites.map((site) => {
      const directSite: DirectSite = {
        id: site.uuid,
        name: site.name,
        route: '/direct/custom-sites/' + site.uuid,
        type: 'Self hosted',
        status: site.widgets_enabled ? 'Live' : 'Disabled',
        properties: site.property_list,
      };

      return directSite;
    });
  }

  private mergeProperties(items: any, properties: any[]) {
    return items.map((item) => {
      if (typeof item.properties === 'object') {
        item.property_ids = item.properties.map((property) => property.id);
      } else {
        item.property_ids = item.properties;
      }
      item.property_list = item.property_ids
        .map((propertyId) => {
          const property = properties.find((p) => p.id === propertyId);
          return property
            ? {
                id: property.id,
                name: property.name,
                picture: property.picture,
              }
            : null;
        })
        .filter((property) => property !== null);
      return item;
    });
  }
}
